// PageStickyNav — reusable sticky sub-navigation used across every
// public page of the site to give guests a quick jump between the
// page's main sections.
//
// Design intent — matches the visual language established by
// StickyHotelNav (used on hotel detail pages):
//   • Cream backdrop with subtle blur so the underlying imagery is
//     hinted at without stealing focus.
//   • Terracotta active indicator + hover treatment.
//   • Horizontal scroll-snap strip on mobile so every anchor stays
//     reachable with a single swipe.
//   • Icon + short SPANISH-first label ("Habitaciones", "Cerca").
//
// Contract:
//   items: Array<{ id: string, label: {en,fr,es}, icon?: LucideIcon }>
//     Only items whose target `#id` exists in the DOM are rendered
//     (auto-filter, so callers can safely declare optional anchors).
//
//   topOffset (px, default 116)
//     Distance from the top of the viewport where the sticky nav
//     sticks. Matches the main site header height so the two never
//     visually overlap.
//
//   scrollOffset (px, default 170)
//     Extra offset applied when jumping to a section so the target
//     heading lands just below both the site header and this nav.
//
//   testIdPrefix (string, default "page-sticky-nav")
//     Prefix for the top-level nav and each anchor button so different
//     pages can be reliably targeted from tests.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import useScrollProgress from "@/hooks/useScrollProgress";

const pick = (m, l) => (m && (m[l] || m.en || m.es || m.fr)) || "";

export default function PageStickyNav({
  items = [],
  topOffset = 116,
  scrollOffset = 170,
  testIdPrefix = "page-sticky-nav",
  ariaLabel = "Sección de la página",
}) {
  const { lang } = useLanguage();
  const location = useLocation();
  const [available, setAvailable] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const stripRef = useRef(null);
  const scrollProgress = useScrollProgress();
  const progressPct = Math.round(scrollProgress * 1000) / 10;

  // Re-detect available anchors when the pathname changes OR items change.
  // Runs on the next tick after mount so route-loaded sections have time
  // to render before we filter. `mounted` gate prevents state updates
  // after unmount when React strict-mode re-runs the effect.
  useEffect(() => {
    let mounted = true;
    const check = () => {
      if (!mounted) return;
      const found = items.filter((it) => {
        if (!it?.id) return false;
        return Boolean(document.getElementById(it.id));
      });
      setAvailable(found);
      if (found.length && !activeId) {
        setActiveId(found[0].id);
      }
    };
    // Two-pass detection: immediate, then delayed for slow-mounting
    // sections (e.g. lists that fetch data before rendering).
    check();
    const t1 = setTimeout(check, 300);
    const t2 = setTimeout(check, 1200);
    return () => {
      mounted = false;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [items, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Highlight the section currently intersecting the "active band" just
  // below the header + this nav.
  useEffect(() => {
    if (!available.length) return undefined;
    const rootMargin = `-${topOffset + 60}px 0px -55% 0px`;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin, threshold: 0 }
    );
    available.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [available, topOffset]);

  // Keep the active pill in view on the mobile scroll-strip.
  useEffect(() => {
    if (!activeId || !stripRef.current) return;
    const btn = stripRef.current.querySelector(`[data-nav-target="${activeId}"]`);
    if (btn && typeof btn.scrollIntoView === "function") {
      btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeId]);

  const rendered = useMemo(() => available, [available]);
  if (!rendered.length) return null;

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - scrollOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <nav
      className="sticky z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#2C2621]/10 relative"
      style={{ top: `${topOffset}px` }}
      data-testid={testIdPrefix}
      aria-label={ariaLabel}
    >
      {/* Reading progress — thin terracotta line pinned to the top edge
          of the sticky nav. Grows from 0 → 100 % as the visitor scrolls
          the current page so it always reads as an accent line sitting
          directly above the section navigation. */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px] pointer-events-none"
        data-testid="reading-progress-bar"
        aria-hidden="true"
      >
        <div
          className="h-full bg-[#C16542] shadow-[0_0_8px_rgba(193,101,66,0.55)] transition-[width] duration-150 ease-out"
          style={{ width: `${progressPct}%` }}
          data-testid="reading-progress-bar-fill"
        />
      </div>
      <div
        ref={stripRef}
        className="max-w-7xl mx-auto px-4 md:px-12 flex items-center gap-1 md:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:snap-none"
      >
        {rendered.map((it) => {
          const Icon = it.icon;
          const isActive = activeId === it.id;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => jumpTo(it.id)}
              data-nav-target={it.id}
              data-testid={`${testIdPrefix}-${it.id}`}
              data-active={isActive}
              className={`snap-start shrink-0 group inline-flex items-center gap-2 py-3.5 md:py-4 px-3 md:px-4 border-b-2 transition-colors whitespace-nowrap text-[11px] md:text-xs tracking-[0.22em] uppercase ${
                isActive
                  ? "border-[#C16542] text-[#C16542]"
                  : "border-transparent text-[#5C5248] hover:text-[#2C2621]"
              }`}
            >
              {Icon ? (
                <Icon
                  className={`w-4 h-4 transition-colors ${
                    isActive ? "text-[#C16542]" : "text-[#5C5248] group-hover:text-[#2C2621]"
                  }`}
                  strokeWidth={1.5}
                />
              ) : null}
              <span>{pick(it.label, lang)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
